/**
 * React Query hooks for fertilizer events.
 * React Query hooks for the corresponding entity.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";
import { fetchApi } from "common/utils/Api";
import {
  FERTILIZER_EVENT_REJECTION_PERIOD_DAYS,
  FERTILIZER_TOTAL_AMOUNT_UNIT,
  FERTILIZER_TYPES,
  FertilizerEvent,
  FertilizerEventPostPutBody,
  NITROGEN_FERTILIZER_TYPE,
  ORGANIC_FERTILIZER_ANIMAL_TYPES,
  ORGANIC_FERTILIZER_APPLICATION_METHOD,
  ORGANIC_FERTILIZER_TYPES,
} from "./fertilizerEventTypes";

// Re-export types for convenience
export {
  FERTILIZER_EVENT_REJECTION_PERIOD_DAYS,
  FERTILIZER_TOTAL_AMOUNT_UNIT,
  FERTILIZER_TYPES,
  fertilizerEventApi,
  NITROGEN_FERTILIZER_TYPE,
  ORGANIC_FERTILIZER_ANIMAL_TYPES,
  ORGANIC_FERTILIZER_APPLICATION_METHOD,
  ORGANIC_FERTILIZER_TYPES,
};
export type { FertilizerEvent, FertilizerEventPostPutBody };

class FertilizerAPI extends API<FertilizerEvent> {
  async getAllFertilizerEventsForFarm(
    farmGuid: string,
    fertilizerApplicationTimeAfter?: string
  ): Promise<FertilizerEvent[]> {
    const fertilizerApplicationTimeAfterParam = fertilizerApplicationTimeAfter
      ? `&fertilizerApplicationTimeAfter=${fertilizerApplicationTimeAfter}`
      : "";
    const res = await fetch(
      `${this.baseUrl}fertilizerApplication?farmGuid=${farmGuid}${fertilizerApplicationTimeAfterParam}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
          "Content-Type": "application/json",
        },
      }
    );
    return (await res.json()) as FertilizerEvent[];
  }

  async createFertilizerEvent(
    fertilizerEvent: FertilizerEventPostPutBody
  ): Promise<{ guid: string }> {
    const res = await fetch(`${this.baseUrl}fertilizerApplication`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(fertilizerEvent),
    });
    return (await res.json()) as { guid: string };
  }
}

const fertilizerEventApi = new FertilizerAPI("fertilizerApplication", "v2");

// Event data changes infrequently after creation - use 5 minute stale time
// to reduce unnecessary refetches while keeping data reasonably fresh
const EVENT_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all fertilizer events for a farm
 */
export function useFertilizerEvents(
  farmGuid: string | undefined,
  fertilizerApplicationTimeAfter?: string
) {
  return useQuery({
    queryKey: [
      ...queryKeys.events.fertilizer.all,
      farmGuid,
      fertilizerApplicationTimeAfter,
    ],
    queryFn: () =>
      fertilizerEventApi.getAllFertilizerEventsForFarm(
        farmGuid ?? "",
        fertilizerApplicationTimeAfter
      ),
    enabled: !!farmGuid,
    staleTime: EVENT_STALE_TIME,
  });
}

/**
 * Fetch a single fertilizer event by guid
 */
export function useFertilizerEvent(guid: string | undefined) {
  return useQuery({
    queryKey: ["fertilizerEvent", guid],
    queryFn: () => fertilizerEventApi.getOne(guid ?? ""),
    enabled: !!guid,
    staleTime: EVENT_STALE_TIME,
  });
}

/**
 * Create a new fertilizer event
 */
export function useCreateFertilizerEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fertilizerEvent,
    }: {
      fertilizerEvent: FertilizerEventPostPutBody;
      farmGuid: string;
    }) => {
      const result = await fertilizerEventApi.createFertilizerEvent(
        fertilizerEvent
      );
      return result;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.fertilizer.all,
      });
      await queryClient.invalidateQueries({
        queryKey: ["autoEvidence", "fertilizer"],
      });
    },
  });
}

/**
 * Update an existing fertilizer event
 */
export function useUpdateFertilizerEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      fertilizerEvent,
    }: {
      fertilizerEvent: FertilizerEvent;
    }) => {
      const result = await fertilizerEventApi.updateOne(fertilizerEvent);
      return { ...fertilizerEvent, ...result };
    },
    onSuccess: async (updatedEvent) => {
      queryClient.setQueryData(
        ["fertilizerEvent", updatedEvent.guid],
        updatedEvent
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.fertilizer.all,
      });
      await queryClient.invalidateQueries({
        queryKey: ["autoEvidence", "fertilizer"],
      });
    },
  });
}

/**
 * Delete a fertilizer event
 */
export function useDeleteFertilizerEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (guid: string) => {
      await fetchApi(
        `fertilizerApplication/${guid}`,
        { method: "DELETE" },
        "v2"
      );
      return guid;
    },
    onSuccess: async (guid) => {
      queryClient.removeQueries({ queryKey: ["fertilizerEvent", guid] });
      await queryClient.invalidateQueries({
        queryKey: queryKeys.events.fertilizer.all,
      });
      await queryClient.invalidateQueries({
        queryKey: ["autoEvidence", "fertilizer"],
      });
    },
  });
}
